import { reflect } from '@effector/reflect';
import { TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DEVICE_PIN_LENGTH } from 'entities/device';
import { $t, breakpoints, translated } from 'shared/lib';
import { createModalForm, ModalFormProps } from 'shared/ui';
import { ActivateDeviceModel } from '../../model';
import cls from './activate-device-modal.module.scss';

export const createActivateDeviceModal = (model: ActivateDeviceModel) => {
	const ModalForm = createModalForm(model);

	const PinField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'activate-device-pin-input',
			maxLength: DEVICE_PIN_LENGTH,
			size: breakpoints.md.map((md) => (md ? '2xl' : 'xl')),
			value: model.form.fields.pin.$value,
			label: translated('activate-device.pin'),
			classNames: {
				root: cls.activateDeviceModalPinField,
				input: cls.activateDeviceModalPinFieldInput,
			},
			errorProps: { 'data-test-id': 'activate-device-pin-input-error' },
			error: combine(
				[model.form.fields.pin.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('activate-device.pin'),
						length: DEVICE_PIN_LENGTH,
					}),
			),
			onChange: (e) => model.form.fields.pin.change(e.target.value),
		},
	});

	const ActivateDeviceModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, md] = useUnit([model.$pending, breakpoints.md]);

		return (
			<ModalForm
				ref={ref}
				title={t('activate-device.title')}
				submitButtonProps={{
					children: t('activate-device.activate'),
					leftSection: <Plus />,
					loading: pending,
					variant: 'success',
					size: md ? 'xl' : 'lg',
					fullWidth: true,
				}}
				size={md ? '2xl' : 'xl'}
				{...props}
			>
				<PinField />
			</ModalForm>
		);
	});

	const ActivateDeviceModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			w: breakpoints.md.map((md) => (md ? 'min-content' : '100%')),
			variant: 'success',
			leftSection: <Plus />,
			children: translated('activate-device.activate'),
			size: breakpoints.md.map((md) => (md ? 'xl' : 'lg')),
			modalData: null,
		},
	});

	return Object.assign(ActivateDeviceModal, {
		Button: ActivateDeviceModalButton,
	});
};
